import { useCallback, useMemo } from 'react';
import { formatAsTwoLinesBarcode } from '@ibk/shared-utils';
import { Currency, TransactionSource } from '../../enums';
function digitsOnly(value) {
    return value.replace(/\D/g, '');
}
function ensureDateTimeOffset(value) {
    if (!value)
        return value;
    const hasOffset = /(?:Z|[+-]\d{2}:\d{2})$/.test(value);
    return hasOffset ? value : `${value}Z`;
}
function isBarcodeDocument(documentDigits) {
    return documentDigits.length === 44;
}
function getBilletAmount(consultation) {
    if (consultation.variant === 'v1') {
        return consultation.billet.amount;
    }
    return consultation.billet.totalUpdated ?? consultation.billet.value ?? 0;
}
function getBilletBeneficiaryName(consultation) {
    if (consultation.variant === 'v1') {
        return consultation.billet.beneficiaryName;
    }
    return consultation.billet.recipient ?? '';
}
function getBilletBankName(consultation) {
    return consultation.variant === 'v2' ? consultation.billet.assignor : '';
}
function getBilletDueDate(consultation) {
    if (consultation.variant === 'v2') {
        return consultation.billet.payDueDate ?? '';
    }
    return consultation.billet.dueDate;
}
function getBilletBarcodeDisplay(consultation) {
    const raw = consultation.variant === 'v1'
        ? (consultation.billet.barCode || consultation.billet.digitableLine || '').trim()
        : (digitsOnly(consultation.document) || consultation.billet.digitable || '').trim();
    return formatAsTwoLinesBarcode(raw);
}
export function useBilletConsultation(consultation) {
    const displayValues = useMemo(() => ({
        amount: consultation ? getBilletAmount(consultation) : 0,
        beneficiaryName: consultation ? getBilletBeneficiaryName(consultation) : '',
        bankName: consultation ? getBilletBankName(consultation) : '',
        dueDate: consultation ? getBilletDueDate(consultation) : '',
        barcodeDisplay: consultation ? getBilletBarcodeDisplay(consultation) : '',
    }), [consultation]);
    const buildPayV2Body = useCallback((v2Consultation, options) => {
        const { billet } = v2Consultation;
        return {
            document: digitsOnly(v2Consultation.document),
            amount: billet.totalUpdated ?? billet.value ?? 0,
            transactionIdAuthorize: billet.transactionIdAuthorize,
            type: billet.type,
            bankslipDetails: {
                beneficiaryName: billet.recipient ?? '',
                beneficiaryDocumentNumber: digitsOnly(billet.documentRecipient ?? ''),
                dueDate: ensureDateTimeOffset(billet.payDueDate ?? ''),
                fineValueCalculated: billet.fineValueCalculated,
                interestValueCalculated: billet.interestValueCalculated,
                discountValue: billet.discountValue,
            },
            credential: options.pin,
            source: TransactionSource.FINTECH_IBK,
            currency: Currency.BRL,
            accountPayablesId: options.accountPayablesId,
        };
    }, []);
    const buildScheduleBody = useCallback((scheduleConsultation, options) => {
        const base = {
            credential: options.pin,
            currency: Currency.BRL,
            source: TransactionSource.FINTECH_IBK,
            paymentDate: options.paymentDate,
            reSchedule: options.reSchedule,
            accountPayablesId: options.accountPayablesId,
        };
        if (scheduleConsultation.variant === 'v1') {
            const { billet } = scheduleConsultation;
            return {
                ...base,
                amount: billet.amount,
                barCode: billet.barCode,
                digitableLine: billet.digitableLine,
                dueDate: billet.dueDate,
            };
        }
        const { billet } = scheduleConsultation;
        const documentDigits = digitsOnly(scheduleConsultation.document);
        return {
            ...base,
            amount: billet.totalUpdated ?? billet.value ?? 0,
            barCode: isBarcodeDocument(documentDigits) ? documentDigits : '',
            digitableLine: billet.digitable,
            dueDate: billet.payDueDate ?? '',
        };
    }, []);
    return {
        ...displayValues,
        buildPayV2Body,
        buildScheduleBody,
    };
}
